// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { Activity, Snowflake, ChevronRight, RefreshCw } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { ErrorBanner, LoadingState } from '../../components/workflow/StatePills'
import { BarChart } from '../../components/charts/Charts'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

// D-OBS-1 P3 — SaaS-admin per-pharmacy latency board. Renders the control-plane
// rollup from /ops/latency (health matrix) and /ops/latency/{id} (drill-down).
// PHI-free. Platform-admin only (the API enforces it).

const HEALTH_FG: Record<api.LatencyHealth, string> = { ok: '#0f7d72', warn: '#a6771a', breach: '#9e3a24', na: '#69788c' }
const HEALTH_BG: Record<api.LatencyHealth, string> = { ok: '#e7f5f3', warn: '#fbf1dc', breach: '#f7e2db', na: '#eef1f5' }
const HEALTH_LABEL: Record<api.LatencyHealth, string> = { ok: 'OK', warn: 'Warn', breach: 'Breach', na: 'No SLO' }

const fmtMs = (ms: number) => (ms >= 1000 ? `${(ms / 1000).toFixed(ms >= 10000 ? 0 : 1)}s` : `${ms}ms`)
const shortOp = (op: string) => op.replace(/^search\./, '').replace(/^ui\./, '').replace(/^ingest\./, '')
const hhmm = (iso: string) => { try { const d = new Date(iso); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` } catch { return '' } }

// D-UI-2: what each column shows, for its header funnel.
const EVENT_FUNNELS: FunnelColumn<api.LatencyDetail['recent_events'][number]>[] = [
  { key: 'when',      label: 'When',      value: e => e.occurred_at },
  { key: 'operation', label: 'Operation', value: e => e.operation },
  { key: 'duration',  label: 'Duration',  value: e => fmtMs(e.duration_ms) },
  { key: 'flags',     label: 'Flags',     value: e => [e.cold_resume ? 'cold' : null, e.status !== 'ok' ? e.status : null].filter(Boolean).join(' ') },
]
const SLO_FUNNELS: FunnelColumn<api.LatencySlo>[] = [
  { key: 'operation', label: 'Operation',  value: s => s.operation },
  { key: 'target',    label: 'Target p95', value: s => fmtMs(s.target_p95_ms) },
  { key: 'warn',      label: 'Warn',       value: s => fmtMs(s.warn_p95_ms) },
  { key: 'breach',    label: 'Breach',     value: s => fmtMs(s.breach_p95_ms) },
]

function HealthChip({ h, label }: { h: api.LatencyHealth; label?: string }) {
  return (
    <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 650, padding: '2px 8px', borderRadius: 999, color: HEALTH_FG[h], background: HEALTH_BG[h] }}>
      {label ?? HEALTH_LABEL[h]}
    </span>
  )
}

export default function LatencyBoard() {
  const [hours, setHours] = useState(24)
  const [matrix, setMatrix] = useState<api.LatencyMatrix | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<api.LatencyPharmacyRow | null>(null)
  const [detail, setDetail] = useState<api.LatencyDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const load = useCallback(() => {
    setLoading(true); setError(null)
    api.getLatencyMatrix(hours).then(setMatrix).catch(e => setError(friendlyError(e))).finally(() => setLoading(false))
  }, [hours])
  useEffect(() => { load() }, [load])

  const openDetail = (row: api.LatencyPharmacyRow) => {
    setSelected(row); setDetail(null); setDetailLoading(true)
    api.getLatencyDetail(row.pharmacy_id, hours).then(setDetail).catch(() => setDetail(null)).finally(() => setDetailLoading(false))
  }

  // union of operations across pharmacies, stable order
  const ops = useMemo(
    () => Array.from(new Set((matrix?.pharmacies ?? []).flatMap(p => p.operations.map(o => o.operation)))).sort(),
    [matrix])
  // D-UI-2: the matrix has one funnel per column, the operation columns included.
  const matrixFunnels = useMemo<FunnelColumn<api.LatencyPharmacyRow>[]>(() => [
    { key: 'pharmacy', label: 'Pharmacy', value: p => p.name || p.pharmacy_id.slice(0, 8) },
    { key: 'overall',  label: 'Overall',  value: p => HEALTH_LABEL[p.worst_health] },
    ...ops.map(op => ({
      key: `op:${op}`, label: shortOp(op),
      value: (p: api.LatencyPharmacyRow) => {
        const c = p.operations.find(o => o.operation === op)
        return c ? fmtMs(c.p95_ms) : null
      },
    })),
  ], [ops])
  const mfx = useTableFunnels(matrix?.pharmacies, matrixFunnels)
  const efx = useTableFunnels(detail?.recent_events, EVENT_FUNNELS)

  if (loading) return <div style={pageStyle}><LoadingState label="Loading latency…" /></div>
  if (error) return <div style={pageStyle}><ErrorBanner message={error} /></div>
  if (!matrix) return null

  return (
    <div style={pageStyle}>
      <header style={headerRow}>
        <div>
          <h1 style={titleStyle}><Activity size={20} style={{ verticalAlign: '-3px', marginRight: 8 }} />Latency</h1>
          <p style={subtitleStyle}>Per-pharmacy request latency — search, grid load and ingest — flagged against SLO before users report it. Cold-resume marks a serverless wake.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={segWrap} role="group" aria-label="Time window">
            {([[24, '24h'], [72, '3d'], [168, '7d']] as [number, string][]).map(([h, l]) => (
              <button key={h} onClick={() => setHours(h)}
                style={{ ...segBtn, ...(hours === h ? segBtnActive : {}) }}>{l}</button>
            ))}
          </div>
          <button onClick={load} style={iconBtn} aria-label="Refresh"><RefreshCw size={15} /></button>
        </div>
      </header>

      {matrix.pharmacies.length === 0 ? (
        <div style={emptyCard}>
          <strong>No latency recorded in this window yet.</strong>
          <p style={{ margin: '6px 0 0', color: '#69788c', fontSize: 13 }}>
            Capture is on for the enabled pharmacies and rolls up on a ~15-minute cycle, so the board fills in as the portal is used.
            The SLO thresholds below are seeded and live.
          </p>
          <SloReference slos={matrix.slos} />
        </div>
      ) : (
        <section style={sectionStyle}>
          <div style={cardTitleStyle}>Health by pharmacy · p95 over {hours >= 168 ? '7 days' : hours >= 72 ? '3 days' : '24 hours'}</div>
          {mfx.chips}
          <div style={{ overflowX: 'auto' }}>
            <table {...mfx.tableProps} style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Pharmacy {mfx.funnel('pharmacy')}</th>
                  <th style={thStyle}>Overall {mfx.funnel('overall')}</th>
                  {ops.map(op => <th key={op} style={{ ...thStyle, textAlign: 'right' }}>{shortOp(op)} {mfx.funnel(`op:${op}`)}</th>)}
                </tr>
              </thead>
              <tbody>
                {mfx.rows.map(p => {
                  const byOp = new Map(p.operations.map(o => [o.operation, o]))
                  return (
                    <tr key={p.pharmacy_id} onClick={() => openDetail(p)} style={{ cursor: 'pointer', background: selected?.pharmacy_id === p.pharmacy_id ? '#f4f8fb' : undefined }}>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{p.name || p.pharmacy_id.slice(0, 8)} <ChevronRight size={13} style={{ verticalAlign: '-2px', color: '#9aa7b8' }} /></td>
                      <td style={tdStyle}><HealthChip h={p.worst_health} /></td>
                      {ops.map(op => {
                        const c = byOp.get(op)
                        return (
                          <td key={op} style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {c ? (
                              <span title={`n=${c.n}${c.cold_resumes ? ` · ${c.cold_resumes} cold` : ''}${c.errors ? ` · ${c.errors} err` : ''}`}
                                style={{ color: HEALTH_FG[c.health], fontWeight: 600 }}>
                                {fmtMs(c.p95_ms)}{c.cold_resumes ? <Snowflake size={11} style={{ verticalAlign: '-1px', marginLeft: 3 }} /> : null}
                              </span>
                            ) : <span style={{ color: '#c3ccd8' }}>—</span>}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <p style={noteStyle}>Click a pharmacy for its trend and recent slow requests. ❄ = a serverless cold-resume was detected on that operation.</p>
        </section>
      )}

      {selected && (
        <section style={sectionStyle}>
          <div style={cardTitleStyle}>{selected.name || selected.pharmacy_id.slice(0, 8)} · detail</div>
          {detailLoading ? <LoadingState label="Loading detail…" />
            : !detail ? <p style={{ color: '#69788c', fontSize: 13 }}>No detail available.</p>
              : (
                <>
                  {detail.series.length === 0 && <p style={{ color: '#69788c', fontSize: 13 }}>No rolled-up buckets in this window yet.</p>}
                  {detail.series.map(s => (
                    <div key={s.operation} style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{s.operation}
                        <span style={{ color: '#69788c', fontWeight: 400 }}> · p95 by hour</span></div>
                      <BarChart
                        data={s.points.map(pt => ({ label: hhmm(pt.bucket_start), value: pt.p95_ms }))}
                        height={160}
                        valueFormat={fmtMs}
                      />
                    </div>
                  ))}
                  {detail.recent_events.length > 0 && (
                    <>
                      <div style={{ ...cardTitleStyle, marginTop: 8 }}>Recent slow requests</div>
                      {efx.chips}
                      <table {...efx.tableProps} style={tableStyle}>
                        <thead><tr>
                          <th style={thStyle}>When {efx.funnel('when')}</th><th style={thStyle}>Operation {efx.funnel('operation')}</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Duration {efx.funnel('duration')}</th><th style={thStyle}>Flags {efx.funnel('flags')}</th>
                        </tr></thead>
                        <tbody>
                          {efx.rows.slice(0, 25).map((e, i) => (
                            <tr key={i}>
                              <td style={tdStyle}>{hhmm(e.occurred_at)}</td>
                              <td style={tdStyle}>{e.operation}{e.route_template ? <span style={{ color: '#9aa7b8' }}> {e.route_template}</span> : null}</td>
                              <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtMs(e.duration_ms)}</td>
                              <td style={tdStyle}>
                                {e.cold_resume ? <span style={{ color: '#4a6fa5' }}><Snowflake size={12} style={{ verticalAlign: '-1px' }} /> cold </span> : null}
                                {e.status !== 'ok' ? <span style={{ color: '#9e3a24' }}>{e.status}</span> : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                  <SloReference slos={detail.slos} />
                </>
              )}
        </section>
      )}
    </div>
  )
}

function SloReference({ slos }: { slos: api.LatencySlo[] }) {
  const fx = useTableFunnels(slos, SLO_FUNNELS)
  if (!slos?.length) return null
  return (
    <details style={{ marginTop: 12 }}>
      <summary style={{ fontSize: 12, color: '#69788c', cursor: 'pointer' }}>SLO thresholds</summary>
      {fx.chips}
      <table {...fx.tableProps} style={{ ...tableStyle, marginTop: 8 }}>
        <thead><tr>
          <th style={thStyle}>Operation {fx.funnel('operation')}</th><th style={{ ...thStyle, textAlign: 'right' }}>Target p95 {fx.funnel('target')}</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Warn {fx.funnel('warn')}</th><th style={{ ...thStyle, textAlign: 'right' }}>Breach {fx.funnel('breach')}</th>
        </tr></thead>
        <tbody>
          {fx.rows.map(s => (
            <tr key={s.operation}>
              <td style={tdStyle}>{s.operation}{!s.enabled ? <span style={{ color: '#9aa7b8' }}> (off)</span> : null}</td>
              <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMs(s.target_p95_ms)}</td>
              <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMs(s.warn_p95_ms)}</td>
              <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMs(s.breach_p95_ms)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  )
}

const pageStyle: CSSProperties = { padding: 24, maxWidth: '100%', margin: '0 auto' }
const headerRow: CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 20 }
const titleStyle: CSSProperties = { fontSize: 22, letterSpacing: '-0.02em', margin: 0 }
const subtitleStyle: CSSProperties = { color: 'var(--color-text-mute, #69788c)', fontSize: 14, margin: '4px 0 0', maxWidth: 680 }
const sectionStyle: CSSProperties = { background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border, #e4e8ef)', borderRadius: 14, padding: 16, marginBottom: 16 }
const cardTitleStyle: CSSProperties = { fontSize: 14, fontWeight: 650, margin: '0 0 12px' }
const emptyCard: CSSProperties = { background: 'var(--color-surface, #fff)', border: '1px dashed var(--color-border, #d2d9e4)', borderRadius: 14, padding: 20, marginBottom: 16 }
const noteStyle: CSSProperties = { fontSize: 12, color: 'var(--color-text-mute, #69788c)', fontStyle: 'italic', marginTop: 10 }
const tableStyle: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 }
const thStyle: CSSProperties = { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-mute, #69788c)', borderBottom: '1px solid var(--color-border, #d2d9e4)', padding: '6px 8px', whiteSpace: 'nowrap' }
const tdStyle: CSSProperties = { padding: '7px 8px', borderBottom: '1px solid var(--color-border, #eef1f5)', whiteSpace: 'nowrap' }
const segWrap: CSSProperties = { display: 'inline-flex', border: '1px solid var(--color-border, #d2d9e4)', borderRadius: 9, overflow: 'hidden' }
const segBtn: CSSProperties = { fontSize: 13, padding: '6px 12px', border: 'none', background: '#fff', cursor: 'pointer', color: '#4a6fa5' }
const segBtnActive: CSSProperties = { background: '#eef4f3', color: '#0f7d72', fontWeight: 650 }
const iconBtn: CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, border: '1px solid var(--color-border, #d2d9e4)', background: '#fff', cursor: 'pointer', color: '#4a6fa5' }
